import { api } from "../../api";
import { h } from "../shared/safe";
import { pill } from "../shared/blocks";
import { CONFLICT_FIELDS, CONFLICT_STATUSES, CONFLICT_STATUS_LABELS, describeProblem, parseConflicts, readConflict, type ConflictItem } from "./factsModel";
import { busyButton, card, labelled, notice, problemNotice, select, textArea, textInput, webLink, whoText } from "./kit";
import { enc, type Workspace } from "./workspace";

// CITATION CONFLICTS: places (directories, listings, sites) that disagree with the approved facts. Recording or resolving a conflict never
// changes an approved fact: it is a record of a disagreement and what was decided about it.

export function conflictsCard(ws: Workspace): HTMLElement {
  const body = h("div");
  const out = card("fx-conflicts", "Citation conflicts", body);
  let items: ConflictItem[] | null = null;
  let loadError: string | null = null;
  let showClosed = false;
  let flash: HTMLElement | null = null;
  let resolving: string | null = null;
  let draft = { field: "phone", conflictingValue: "", sourceName: "", sourceUrl: "", note: "" };
  const form = h("form", { class: "fx-form", novalidate: true, "aria-label": "Record a citation conflict" });

  async function load() {
    try {
      const list = parseConflicts(await api<unknown>(`/v1/clients/${enc(ws.clientId)}/citation-conflicts`));
      if (!ws.life.alive()) return;
      items = list;
      loadError = list ? null : "The server sent a list this page does not understand.";
    } catch (e) {
      if (!ws.life.alive()) return;
      loadError = describeProblem(e, "Citation conflicts could not be loaded").message;
    }
    paint();
  }

  function buildForm() {
    const add = busyButton({ label: "Record conflict", busyLabel: "Recording…", id: "fx-cc-add", type: "submit", onClick: () => addItem() });
    form.onsubmit = (e) => {
      e.preventDefault();
      void add.click();
    };
    form.replaceChildren(
      h(
        "div",
        { class: "fx-grid" },
        labelled("Which fact disagrees?", select(CONFLICT_FIELDS, draft.field, { name: "field", onChange: (v) => void (draft.field = v) })),
        labelled("What the other place says", textInput(draft.conflictingValue, { name: "conflictingValue", onInput: (v) => void (draft.conflictingValue = v) })),
        labelled("Where (directory or site name)", textInput(draft.sourceName, { name: "sourceName", onInput: (v) => void (draft.sourceName = v) })),
        labelled("Address of the listing (optional)", textInput(draft.sourceUrl, { name: "sourceUrl", onInput: (v) => void (draft.sourceUrl = v), placeholder: "https://" })),
        labelled("Note (optional)", textArea(draft.note, { name: "note", rows: 2, onInput: (v) => void (draft.note = v) }), { wide: true }),
      ),
      h("div", { class: "btn-group" }, add),
      h("div", { id: "fx-cc-form-result", "aria-live": "polite" }),
    );
  }

  async function addItem() {
    const slot = form.querySelector("#fx-cc-form-result")!;
    if (draft.conflictingValue.trim() === "" || draft.sourceName.trim() === "") return void slot.replaceChildren(notice("bad", "Say what the other place shows and where. Nothing was recorded."));
    try {
      const res = await api<{ conflict?: unknown; existing?: boolean }>(`/v1/clients/${enc(ws.clientId)}/citation-conflicts`, { method: "POST", body: JSON.stringify({ field: draft.field, conflictingValue: draft.conflictingValue.trim(), sourceName: draft.sourceName.trim(), ...(draft.sourceUrl.trim() ? { sourceUrl: draft.sourceUrl.trim() } : {}), ...(draft.note.trim() ? { note: draft.note.trim() } : {}) }) });
      if (!ws.life.alive()) return;
      const c = readConflict(res.conflict);
      draft = { ...draft, conflictingValue: "", sourceName: "", sourceUrl: "", note: "" };
      buildForm();
      form.querySelector("#fx-cc-form-result")?.replaceChildren(notice(res.existing ? "info" : "ok", res.existing ? "That conflict was already recorded; nothing was added." : `Recorded${c ? ` (${c.field})` : ""}. The approved fact was not changed.`));
      await load();
      await ws.refreshFacts();
    } catch (e) {
      if (!ws.life.alive()) return;
      form.querySelector("#fx-cc-form-result")?.replaceChildren(problemNotice(describeProblem(e, "Not recorded")));
    }
  }

  function itemBlock(c: ConflictItem): HTMLElement {
    const open = c.status === "open";
    const li = h(
      "li",
      { class: `fx-cc-item${open ? "" : " fx-cc-item--closed"}`, dataset: { conflictId: c.id, status: c.status, field: c.field } },
      h("div", { class: "fx-unr-head" }, h("strong", null, c.field), pill(CONFLICT_STATUS_LABELS[c.status] ?? c.status, open ? "warn" : "neutral")),
      h("dl", { class: "deflist" }, h("dt", null, "Approved (now)"), h("dd", null, c.currentApproved), h("dt", null, "The other place says"), h("dd", null, c.conflictingValue), h("dt", null, "Where"), h("dd", null, c.sourceName, c.sourceUrl ? [" · ", webLink(c.sourceUrl)] : null)),
      c.changedSinceRecorded ? h("div", { class: "muted fx-small" }, `The approved value has changed since this was recorded (it was "${c.approvedWhenRecorded}").`) : null,
      c.note ? h("div", { class: "muted fx-small" }, `Note: ${c.note}`) : null,
      !open ? h("div", { class: "muted fx-small" }, `${CONFLICT_STATUS_LABELS[c.status] ?? c.status} by ${whoText(c.resolvedBy)}${c.resolvedAt ? ` · ${c.resolvedAt.text}` : ""}${c.resolutionNote ? ` · ${c.resolutionNote}` : ""}`) : null,
    );
    if (resolving === c.id) li.append(resolveForm(c));
    else li.append(h("div", { class: "btn-group" }, h("button", { type: "button", class: "secondary", dataset: { action: "resolve" }, "aria-label": `${open ? "Resolve" : "Change"} the ${c.field} conflict from ${c.sourceName}`, on: { click: () => { resolving = c.id; paint(); out.querySelector<HTMLElement>(`[data-conflict-id="${c.id}"] select`)?.focus(); } } }, open ? "Resolve…" : "Change status…")));
    return li;
  }

  function resolveForm(c: ConflictItem): HTMLElement {
    let status = c.status === "open" ? "resolved" : c.status;
    let note = "";
    const err = h("div", { "aria-live": "polite" });
    const go = busyButton({
      label: "Save",
      busyLabel: "Saving…",
      id: `fx-cc-save-${c.id}`,
      onClick: async () => {
        try {
          await api(`/v1/clients/${enc(ws.clientId)}/citation-conflicts/${enc(c.id)}`, { method: "PATCH", body: JSON.stringify({ status, ...(note.trim() ? { resolutionNote: note.trim() } : {}) }) });
          if (!ws.life.alive()) return;
          resolving = null;
          flash = notice("ok", `Conflict marked ${CONFLICT_STATUS_LABELS[status] ?? status}. The approved fact was not changed.`);
          await load();
          await ws.refreshFacts();
        } catch (e) {
          if (!ws.life.alive()) return;
          err.replaceChildren(problemNotice(describeProblem(e, "Not saved")));
        }
      },
    });
    return h("div", { class: "fx-promote", role: "group", "aria-label": "Resolve citation conflict" }, labelled("Status", select(CONFLICT_STATUSES.map((s): [string, string] => [s, CONFLICT_STATUS_LABELS[s] ?? s]), status, { name: "status", onChange: (v) => void (status = v) })), labelled("What was decided (optional)", textInput("", { name: "resolutionNote", onInput: (v) => void (note = v) })), err, h("div", { class: "btn-group" }, go, h("button", { type: "button", class: "secondary", on: { click: () => { resolving = null; paint(); } } }, "Cancel")));
  }

  // The form is placed once and never moved: a repaint of the lists must not take the keyboard away from someone who is typing in it.
  const flashHost = h("div", { "aria-live": "polite" });
  const listHost = h("div");
  body.append(h("p", { class: "muted" }, "Places that disagree with the approved facts. Recording or resolving a conflict never changes an approved fact."), flashHost, h("h3", null, "Record a conflict"), form, listHost);

  function paint() {
    const open = items?.filter((i) => i.status === "open") ?? [];
    const closed = items?.filter((i) => i.status !== "open") ?? [];
    flashHost.replaceChildren(...(flash ? [flash] : []));
    listHost.replaceChildren(
      h("h3", null, `Open conflicts (${open.length})`),
      loadError
        ? h("div", { class: "error-banner", role: "alert" }, loadError, " ", h("button", { type: "button", class: "secondary", on: { click: () => void load() } }, "Try again"))
        : items === null
          ? h("p", { class: "muted" }, "Loading…")
          : open.length === 0
            ? h("p", { class: "muted" }, "No open conflicts.")
            : h("ul", { class: "fx-cc-list", "aria-label": "Open citation conflicts" }, ...open.map(itemBlock)),
      ...(closed.length
        ? [h("div", null, h("button", { type: "button", class: "secondary", id: "fx-cc-toggle-closed", "aria-expanded": String(showClosed), on: { click: () => { showClosed = !showClosed; paint(); } } }, showClosed ? "Hide closed conflicts" : `Show closed conflicts (${closed.length})`), showClosed ? h("ul", { class: "fx-cc-list", "aria-label": "Closed citation conflicts" }, ...closed.map(itemBlock)) : null)]
        : []),
    );
  }

  buildForm();
  void load();
  paint();
  return out;
}
